import { jsx as _jsx } from "react/jsx-runtime";
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { cva } from 'class-variance-authority';
const RING_COLOR_CLASS = {
    base: 'ring-background',
    quiet: 'ring-[var(--layer-subtle)]',
    subtle: 'ring-[var(--layer-faint)]',
};
/**
 * Maps a ring color to the next level up for hover state.
 * `base` → `subtle`, `subtle` → `quiet`. `quiet` has no step-up.
 */
const HOVER_RING_STEP_UP = {
    base: 'group-hover/alert-dot-hover-container:ring-[var(--layer-faint)]',
    subtle: 'group-hover/alert-dot-hover-container:ring-[var(--layer-subtle)]',
};
const getAlertDotClasses = cva('rounded-full flex-none inline-flex', {
    variants: {
        size: {
            sm: 'size-1.5',
            md: 'size-2.5',
        },
        tone: {
            info: 'bg-super-strong',
            success: 'bg-positive-strong',
            error: 'bg-negative-strong',
            warning: 'bg-warning-strong',
            neutral: 'bg-inverse',
        },
    },
    defaultVariants: {
        size: 'sm',
        tone: 'info',
    },
});
/**
 * Compact attention or semantic status dot with sm/md sizes, optional background-matching rings,
 * and an optional accessible label shown in a tooltip.
 *
 * @category components
 * @keywords alert, dot, components, alertdot, component, unread, new, activity, attention,
 *   indicator, status, health, connection, info, success, warning, error, neutral, tooltip, label, size, sm,
 *   md, ring, ringcolor, super, badge, content-item
 * @whenToUse Use AlertDot for compact unread/activity attention indicators or semantic status dots
 *   alongside text, icons, or controls. Keep state-to-tone and state-to-label mapping in the
 *   feature that owns the state. The optional `ringColor` lifts the dot off a matching background.
 *   Use Badge when persistent visible status text fits the layout.
 * @guidelineDo
 *   - Use the default `sm` size for inline indicators in sidebar items, list rows, and notification
 *     badges where space is tight
 *   - Use `md` size for more prominent indicators in cards, dashboards, and library items where the
 *     dot needs to draw clear visual attention
 *   - Pass `ringColor` matching the surrounding background color so the concentric ring creates a
 *     clear visual cutout that separates the dot from its container
 *   - Wrap the parent element with the `group/alert-dot-hover-container` Tailwind group class when
 *     you want the ring to step up one color level on hover
 *   - Keep state-to-tone and state-to-label mapping in the feature that owns the state
 *   - Use `info`, `success`, `warning`, `error`, and `neutral` for status tones
 *   - Pair semantic tones with persistent visible status text or equivalent ARIA on the surrounding
 *     UI; tooltip text is supplementary
 *   - Set `isTooltipFocusable={false}` when a labeled dot is inside a button or link, and
 *     expose the status through that parent control's accessible name or description
 * @guidelineDont
 *   - Don't treat the dot or its transient tooltip as a replacement for persistent status text or
 *     equivalent ARIA
 *   - Don't pass a `ringColor` value that does not match the actual background behind the dot —
 *     mismatched ring colors break the visual cutout effect
 *   - Don't place AlertDot without ensuring sufficient contrast between the chosen tone and its
 *     surrounding context
 *   - Don't use semantic tones or tooltip hover as the sole way to communicate critical status
 *   - Don't leave a labeled dot focusable inside another interactive control
 * @accessibility
 *   - Without a non-empty `label`, AlertDot is decorative and hidden from assistive technology
 *   - With `label`, the dot is an accessible image named by that text and is focusable by
 *     default; the Aether Tooltip supplies hover/focus behavior, `aria-describedby`, and Escape
 *     dismissal
 *   - When used inside a button or link, set `isTooltipFocusable={false}` and add the status to the
 *     parent control's accessible name/description or nearby status text
 *   - The hover ring step-up animation is CSS-only and does not affect focus or keyboard state — no
 *     additional keyboard handling is required
 * @relatedComponents
 *   - badge
 *   - content-item
 */
export function AlertDot({ size, tone, ringColor, label, isTooltipFocusable = true, 'data-testid': dataTestId, }) {
    const dot = (_jsx("span", { "aria-hidden": true, "data-testid": dataTestId, className: getAlertDotClasses({ size, tone }) }));
    const renderedDot = ringColor ? (_jsx("span", { className: "inline-flex rounded-full ring-[1.5px] ring-background", children: _jsx("span", { className: `inline-flex rounded-full ring-[1.5px] ${HOVER_RING_STEP_UP[ringColor] ?? ''} ${RING_COLOR_CLASS[ringColor]}`, children: dot }) })) : (dot);
    if (!label)
        return renderedDot;
    return (_jsx(Tooltip, { content: label, children: _jsx("span", { role: "img", "aria-label": label, className: `inline-flex rounded-full ${isTooltipFocusable ? 'interactable' : ''}`, tabIndex: isTooltipFocusable ? 0 : undefined, children: renderedDot }) }));
}
//# sourceMappingURL=index.js.map